import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { 
  XMarkIcon,
  PlayIcon,
  CheckIcon,
  CircleStackIcon,
  ServerIcon
} from '@heroicons/react/24/outline';
import { serverAPI } from '../services/api';

const SelectiveBackupModal = ({ isOpen, onClose }) => {
  const [selectedDatabases, setSelectedDatabases] = useState([]);
  const [includePortainer, setIncludePortainer] = useState(false);
  const [selectAll, setSelectAll] = useState(false);
  const queryClient = useQueryClient();

  const { data: databasesData, isLoading: loadingDatabases } = useQuery(
    'available-databases',
    serverAPI.getAvailableDatabases,
    { enabled: isOpen }
  );

  const selectiveBackupMutation = useMutation(
    ({ databases, includePortainer }) => 
      serverAPI.runSelectiveBackup(databases, includePortainer),
    {
      onSuccess: (response) => {
        alert('Backup seletivo iniciado com sucesso! Você receberá um e-mail ao finalizar.');
        onClose();
        // Invalidar dados para atualizar a lista
        setTimeout(() => {
          queryClient.invalidateQueries('backups');
        }, 5000);
      },
      onError: (error) => {
        alert('Erro ao executar backup: ' + (error.response?.data?.error || error.message));
      }
    }
  );

  const databases = databasesData?.data?.databases || [];

  useEffect(() => {
    if (selectAll) {
      setSelectedDatabases(databases.map(db => db.name));
    } else {
      setSelectedDatabases([]);
    }
  }, [selectAll, databases]);

  const handleDatabaseToggle = (databaseName) => {
    setSelectedDatabases(prev => {
      if (prev.includes(databaseName)) {
        return prev.filter(name => name !== databaseName);
      } else {
        return [...prev, databaseName];
      }
    });
  };

  const handleSubmit = () => {
    if (selectedDatabases.length === 0 && !includePortainer) {
      alert('Selecione pelo menos uma base de dados ou o Portainer para fazer backup');
      return;
    }

    selectiveBackupMutation.mutate({
      databases: selectedDatabases,
      includePortainer: includePortainer
    });
  };

  const handleSelectAllToggle = () => {
    setSelectAll(!selectAll);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-10 mx-auto p-6 border w-full max-w-2xl shadow-lg rounded-lg bg-white">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-medium text-gray-900">
              Backup Seletivo
            </h3>
            <p className="text-sm text-gray-500 mt-1">
              Selecione as bases de dados e serviços para fazer backup
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="space-y-6">
          {/* PostgreSQL Databases */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center space-x-2">
                <CircleStackIcon className="h-5 w-5 text-blue-600" />
                <h4 className="text-md font-medium text-gray-900">
                  Bases de Dados PostgreSQL
                </h4>
              </div>
              <button
                onClick={handleSelectAllToggle}
                className="text-sm text-blue-600 hover:text-blue-700"
              >
                {selectAll ? 'Desmarcar Todas' : 'Selecionar Todas'}
              </button>
            </div>

            {loadingDatabases ? (
              <div className="space-y-2">
                {[1, 2, 3, 4, 5].map(i => (
                  <div key={i} className="animate-pulse flex items-center space-x-3 p-3 border rounded-lg">
                    <div className="h-4 w-4 bg-gray-200 rounded"></div>
                    <div className="h-4 bg-gray-200 rounded w-1/3"></div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="max-h-48 overflow-y-auto space-y-2">
                {databases.map((database) => (
                  <label
                    key={database.name}
                    className="flex items-center space-x-3 p-3 border rounded-lg hover:bg-gray-50 cursor-pointer"
                  >
                    <div className="flex-shrink-0">
                      <input
                        type="checkbox"
                        checked={selectedDatabases.includes(database.name)}
                        onChange={() => handleDatabaseToggle(database.name)}
                        className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-medium text-gray-900">
                          {database.name}
                        </p>
                        {database.size && database.size !== 'N/A' && (
                          <span className="text-xs font-medium text-blue-600 bg-blue-50 px-2 py-1 rounded-full">
                            {database.size}
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-500">
                        {database.description}
                      </p>
                    </div>
                    {selectedDatabases.includes(database.name) && (
                      <CheckIcon className="h-4 w-4 text-green-500" />
                    )}
                  </label>
                ))}
              </div>
            )}

            {databases.length === 0 && !loadingDatabases && (
              <p className="text-sm text-gray-500 text-center py-4">
                Nenhuma base de dados encontrada
              </p>
            )}
          </div>

          {/* Portainer Option */}
          <div>
            <div className="flex items-center space-x-2 mb-3">
              <ServerIcon className="h-5 w-5 text-purple-600" />
              <h4 className="text-md font-medium text-gray-900">
                Serviços
              </h4>
            </div>

            <label className="flex items-center space-x-3 p-3 border rounded-lg hover:bg-gray-50 cursor-pointer">
              <div className="flex-shrink-0">
                <input
                  type="checkbox"
                  checked={includePortainer}
                  onChange={(e) => setIncludePortainer(e.target.checked)}
                  className="h-4 w-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900">
                  Portainer
                </p>
                <p className="text-xs text-gray-500">
                  Backup das configurações e stacks do Portainer
                </p>
              </div>
              {includePortainer && (
                <CheckIcon className="h-4 w-4 text-green-500" />
              )}
            </label>
          </div>

          {/* Selection Summary */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h5 className="text-sm font-medium text-blue-900 mb-2">
              Resumo da Seleção
            </h5>
            <div className="space-y-1 text-sm text-blue-700">
              <p>
                <strong>Bases de dados:</strong> {selectedDatabases.length} selecionada(s)
              </p>
              {selectedDatabases.length > 0 && (
                <div className="text-xs space-y-1">
                  {selectedDatabases.map(dbName => {
                    const dbInfo = databases.find(db => db.name === dbName);
                    return (
                      <div key={dbName} className="flex justify-between">
                        <span>{dbName}</span>
                        <span className="font-medium">
                          {dbInfo?.size && dbInfo.size !== 'N/A' ? dbInfo.size : 'N/A'}
                        </span>
                      </div>
                    );
                  })}
                  {selectedDatabases.length > 1 && (
                    <div className="pt-1 border-t border-blue-200 flex justify-between font-medium">
                      <span>Total aproximado:</span>
                      <span>
                        {(() => {
                          const totalBytes = selectedDatabases.reduce((sum, dbName) => {
                            const dbInfo = databases.find(db => db.name === dbName);
                            return sum + (dbInfo?.sizeBytes || 0);
                          }, 0);
                          
                          if (totalBytes === 0) return 'N/A';
                          
                          const units = ['B', 'KB', 'MB', 'GB', 'TB'];
                          let size = totalBytes;
                          let unitIndex = 0;
                          
                          while (size >= 1024 && unitIndex < units.length - 1) {
                            size /= 1024;
                            unitIndex++;
                          }
                          
                          return `${size.toFixed(1)} ${units[unitIndex]}`;
                        })()
                        }
                      </span>
                    </div>
                  )}
                </div>
              )}
              <p>
                <strong>Portainer:</strong> {includePortainer ? 'Sim' : 'Não'}
              </p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end space-x-4 mt-6 pt-4 border-t">
          <button
            onClick={onClose}
            className="btn-secondary"
          >
            Cancelar
          </button>
          <button
            onClick={handleSubmit}
            disabled={selectiveBackupMutation.isLoading || (selectedDatabases.length === 0 && !includePortainer)}
            className="btn-primary flex items-center space-x-2"
          >
            <PlayIcon className="h-4 w-4" />
            <span>
              {selectiveBackupMutation.isLoading ? 'Executando...' : 'Executar Backup'}
            </span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default SelectiveBackupModal;